import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  Progress,
  Badge,
  Button,
  Select,
  Input,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  IconButton,
  Tooltip,
  Flex,
} from "@chakra-ui/react";
import {
  FiTrendingUp,
  FiTrendingDown,
  FiTarget,
  FiBarChart,
  FiCalendar,
  FiRefreshCw,
  FiDownload,
  FiEye,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiAward,
  FiUsers,
  FiClock,
} from "react-icons/fi";
import {
  useGetPerformanceInsights,
  useGetPerformanceRanking,
  useCreatePerformanceGoal,
  useGetEmployeeGoals,
  useUpdatePerformanceGoal,
} from "../../services/performanceService";
import { useGetEmployees } from "../../services/employeeService";
import { usePlanRestriction } from "../../context/PlanRestrictionContext";
import BranchAccessRestriction from "../../components/BranchAccessRestriction";
import BranchFilter from "../../components/BranchFilter";

const PerformanceReports = ({ selectedBranch: propSelectedBranch }) => {
  const { canAccessFeature } = usePlanRestriction();
  
  const [selectedPeriod, setSelectedPeriod] = useState("monthly");
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  const [selectedBranch, setSelectedBranch] = useState(propSelectedBranch || "all");
  const [dateRange, setDateRange] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedGoalEmployee, setSelectedGoalEmployee] = useState("");
  const [goalForm, setGoalForm] = useState({
    goal_type: "productivity",
    target_value: 85.0,
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    description: "",
  });
  const toast = useToast();

    // Fetch data with branch filtering
  const { 
    performanceInsightsData, 
    performanceInsightsIsLoading, 
    performanceInsightsError 
  } = useGetPerformanceInsights({
    filters: { ...(selectedBranch !== 'all' && { branch_id: selectedBranch }) }
  });
  const { 
    performanceRankingData, 
    performanceRankingIsLoading, 
    performanceRankingError 
  } = useGetPerformanceRanking({
    filters: {
      report_type: selectedPeriod,
      limit: 10,
      ...(selectedBranch !== 'all' && { branch_id: selectedBranch })
    }
  });
  const { getEmployeesData } = useGetEmployees({
    enabled: true,
    filters: { ...(selectedBranch !== 'all' && { branch_id: selectedBranch }) }
  });

  // Goal management hooks
  const { createGoalPayload, createGoalIsLoading } = useCreatePerformanceGoal(() => {
    toast({
      title: "Goal created",
      description: "Performance goal has been created successfully",
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
    onClose();
    setGoalForm({
      goal_type: "productivity",
      target_value: 85.0,
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      description: "",
    });
  });

  const { employeeGoalsData, employeeGoalsIsLoading } = useGetEmployeeGoals({
    employeeId: selectedEmployee !== "all" ? selectedEmployee : null,
    enabled: selectedEmployee !== "all",
    filters: { status: "active" }
  });

  const { updateGoalPayload, updateGoalIsLoading } = useUpdatePerformanceGoal(() => {
    toast({
      title: "Goal updated",
      description: "Performance goal has been updated successfully",
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  });

  const handleCreateGoal = async () => {
    try {
      await createGoalPayload({
        ...goalForm,
        employee_id: selectedGoalEmployee
      });
    } catch (error) {
      console.error("Error creating goal:", error);
    }
  };

  const handleUpdateGoal = async (goalId, currentValue) => {
    try {
      await updateGoalPayload({
        goal_id: goalId,
        current_value: currentValue,
        status: "active"
      });
    } catch (error) {
      console.error("Error updating goal:", error);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "green";
    if (score >= 60) return "yellow";
    if (score >= 40) return "orange";
    return "red";
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return "Excellent";
    if (score >= 60) return "Good";
    if (score >= 40) return "Average";
    return "Poor";
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return "green";
    if (percentage >= 60) return "yellow";
    if (percentage >= 40) return "orange";
    return "red";
  };

  // Feature access is now only restricted by user role (branch vs main branch)
  // All Main Branch users have access to Performance Reports regardless of plan



  return (
    <Box w="100%" bg="gray.50" minH="100vh" p={6}>
      {/* Header */}
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between" align="center">
          <VStack align="start" spacing={2}>
            <Heading size="lg" color="gray.900" fontWeight="600">
              Performance Reports
            </Heading>
            <Text color="gray.600" fontSize="md">
              Track performance metrics and manage employee goals
            </Text>
          </VStack>
          <HStack spacing={3}>
            <Button
              leftIcon={<FiRefreshCw />}
              colorScheme="blue"
              variant="outline"
              onClick={() => window.location.reload()}
            >
              Refresh
            </Button>
            <Button
              leftIcon={<FiDownload />}
              colorScheme="green"
              variant="outline"
            >
              Export Report
            </Button>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={onOpen}
            >
              Create Goal
            </Button>
          </HStack>
        </HStack>

                 {/* Filters */}
         <Card>
           <CardBody>
             <HStack spacing={4} wrap="wrap">
               <Select
                 value={selectedPeriod}
                 onChange={(e) => setSelectedPeriod(e.target.value)}
                 maxW="150px"
               >
                 <option value="daily">Daily</option>
                 <option value="weekly">Weekly</option>
                 <option value="monthly">Monthly</option>
                 <option value="quarterly">Quarterly</option>
               </Select>
               
                               {/* Branch filter is now handled at the parent level */}
               
               <Select
                 value={selectedEmployee}
                 onChange={(e) => setSelectedEmployee(e.target.value)}
                 maxW="200px"
               >
                 <option value="all">All Employees</option>
                 {(getEmployeesData?.employees || []).map((employee) => (
                   <option key={employee.employee_id} value={employee.employee_id}>
                     {employee.first_name} {employee.last_name}
                   </option>
                 ))}
               </Select>
               
               <Input
                 type="date"
                 value={dateRange.start_date}
                 onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                 maxW="150px"
               />
               
               <Input
                 type="date"
                 value={dateRange.end_date}
                 onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                 maxW="150px"
               />
             </HStack>
           </CardBody>
         </Card>

        {/* Performance Summary */}
        <SimpleGrid columns={{ base: 1, sm: 2, md: 2, lg: 4 }} spacing={6}>
          <StatCard
            title="Total Employees"
            value={performanceInsightsData?.performance_summary?.total_employees || 0}
            color="blue"
            icon={<FiUsers />}
            isLoading={performanceInsightsIsLoading}
          />
          <StatCard
            title="Average Productivity"
            value={performanceInsightsData?.performance_summary?.average_productivity || 0}
            suffix="%"
            color="green"
            icon={<FiTarget />}
            isLoading={performanceInsightsIsLoading}
          />
          <StatCard
            title="High Performers"
            value={performanceInsightsData?.performance_summary?.high_performers || 0}
            color="purple"
            icon={<FiAward />}
            isLoading={performanceInsightsIsLoading}
          />
          <StatCard
            title="Low Performers"
            value={performanceInsightsData?.performance_summary?.low_performers || 0}
            color="orange"
            icon={<FiClock />}
            isLoading={performanceInsightsIsLoading}
          />
        </SimpleGrid>

        {/* Organization Insights */}
        <Card>
          <CardBody>
            <VStack align="start" spacing={4}>
              <Heading size="md" color="gray.900">
                Organization Insights
              </Heading>
              {performanceInsightsIsLoading ? (
                <Box textAlign="center" py={8}>
                  <Spinner size="md" color="blue.500" />
                  <Text mt={2} color="gray.600">Loading insights...</Text>
                </Box>
              ) : performanceInsightsError ? (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">Failed to load insights. Please try again.</Text>
                </Alert>
              ) : performanceInsightsData?.organization_insights ? (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                  {Array.isArray(performanceInsightsData.organization_insights) && performanceInsightsData.organization_insights.map((insight, index) => (
                    <Alert key={index} status="info" borderRadius="md">
                      <AlertIcon />
                      <Text fontSize="sm">{insight}</Text>
                    </Alert>
                  ))}
                </SimpleGrid>
              ) : (
                <Text color="gray.500" fontSize="sm">No insights available</Text>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Performance Rankings */}
        <Card>
          <CardBody>
            <VStack align="start" spacing={4}>
              <Heading size="md" color="gray.900">
                Top Performers
              </Heading>
              
              {performanceRankingIsLoading ? (
                <Box textAlign="center" py={8}>
                  <Spinner size="md" color="blue.500" />
                  <Text mt={2} color="gray.600">Loading rankings...</Text>
                </Box>
              ) : performanceRankingError ? (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">Failed to load performance rankings. Please try again.</Text>
                </Alert>
              ) : (
                <Box w="100%" overflowX="auto">
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Rank</Th>
                        <Th>Employee</Th>
                        <Th>Productivity Score</Th>
                        <Th>Attendance Rate</Th>
                        <Th>Efficiency Rate</Th>
                        <Th>Work Hours</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {Array.isArray(performanceRankingData?.top_performers) && performanceRankingData.top_performers.length > 0 ? (
                        performanceRankingData.top_performers.map((employee, index) => (
                          <Tr key={employee.employee_id}>
                            <Td>
                              <Badge
                                colorScheme={index < 3 ? "gold" : "gray"}
                                variant="solid"
                                borderRadius="full"
                                px={3}
                              >
                                #{employee.rank}
                              </Badge>
                            </Td>
                            <Td fontWeight="500">{employee.employee_name}</Td>
                            <Td>
                              <HStack spacing={2}>
                                <Text fontWeight="600">
                                  {employee.productivity_score.toFixed(1)}%
                                </Text>
                                <Badge
                                  colorScheme={getScoreColor(employee.productivity_score)}
                                  variant="subtle"
                                >
                                  {getScoreLabel(employee.productivity_score)}
                                </Badge>
                              </HStack>
                            </Td>
                            <Td>{employee.attendance_rate.toFixed(1)}%</Td>
                            <Td>{employee.efficiency_rate.toFixed(1)}%</Td>
                            <Td>{employee.work_hours.toFixed(1)}h</Td>
                            <Td>
                              <HStack spacing={2}>
                                <Tooltip label="View Details">
                                  <IconButton
                                    icon={<FiEye />}
                                    size="sm"
                                    variant="ghost"
                                  />
                                </Tooltip>
                                <Tooltip label="Create Goal">
                                  <IconButton
                                    icon={<FiPlus />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setSelectedGoalEmployee(employee.employee_id);
                                      onOpen();
                                    }}
                                  />
                                </Tooltip>
                              </HStack>
                            </Td>
                          </Tr>
                        ))
                      ) : (
                        <Tr>
                          <Td colSpan={7} textAlign="center" py={8}>
                            <Text color="gray.500" fontSize="sm">No performance data available</Text>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Employee Goals */}
        {selectedEmployee !== "all" && (
          <Card>
            <CardBody>
              <VStack align="start" spacing={4}>
                <Heading size="md" color="gray.900">
                  Employee Goals
                </Heading>
                
                {employeeGoalsIsLoading ? (
                  <Box textAlign="center" py={8}>
                    <Spinner size="md" color="blue.500" />
                    <Text mt={2} color="gray.600">Loading goals...</Text>
                  </Box>
                ) : employeeGoalsData?.length > 0 ? (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                    {Array.isArray(employeeGoalsData) && employeeGoalsData.map((goal) => (
                      <Card key={goal.goal_id} variant="outline">
                        <CardBody>
                          <VStack align="start" spacing={3}>
                            <HStack justify="space-between" w="100%">
                              <Badge colorScheme="blue" variant="subtle">
                                {goal.goal_type}
                              </Badge>
                              <Badge
                                colorScheme={goal.status === "active" ? "green" : "gray"}
                                variant="subtle"
                              >
                                {goal.status}
                              </Badge>
                            </HStack>
                            
                            <Text fontWeight="600" fontSize="lg">
                              {goal.description}
                            </Text>
                            
                            <VStack align="start" spacing={2} w="100%">
                              <HStack justify="space-between" w="100%">
                                <Text fontSize="sm" color="gray.600">
                                  Progress: {goal.progress_percentage.toFixed(1)}%
                                </Text>
                                <Text fontSize="sm" color="gray.600">
                                  {goal.current_value}/{goal.target_value}
                                </Text>
                              </HStack>
                              <Progress
                                value={goal.progress_percentage}
                                colorScheme={getProgressColor(goal.progress_percentage)}
                                size="sm"
                                w="100%"
                              />
                            </VStack>
                            
                            <HStack spacing={2} w="100%">
                              <Button
                                size="sm"
                                colorScheme="blue"
                                variant="outline"
                                leftIcon={<FiEdit />}
                                onClick={() => handleUpdateGoal(goal.goal_id, goal.current_value + 5)}
                                isLoading={updateGoalIsLoading}
                              >
                                Update Progress
                              </Button>
                            </HStack>
                          </VStack>
                        </CardBody>
                      </Card>
                    ))}
                  </SimpleGrid>
                ) : (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Text>No active goals found for this employee.</Text>
                  </Alert>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Recommendations */}
        {performanceInsightsData?.recommendations && (
          <Card>
            <CardBody>
              <VStack align="start" spacing={4}>
                <Heading size="md" color="gray.900">
                  Recommendations
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                  {Array.isArray(performanceInsightsData?.recommendations) && performanceInsightsData.recommendations.map((rec, index) => (
                    <Box
                      key={index}
                      p={4}
                      bg="green.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="green.200"
                    >
                      <Text fontSize="sm" color="green.800">
                        {rec}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>

      {/* Create Goal Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Performance Goal</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Employee</FormLabel>
                <Select
                  placeholder="Select Employee"
                  value={selectedGoalEmployee}
                  onChange={(e) => setSelectedGoalEmployee(e.target.value)}
                >
                  {(getEmployeesData?.employees || []).map((employee) => (
                    <option key={employee.employee_id} value={employee.employee_id}>
                      {employee.first_name} {employee.last_name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Goal Type</FormLabel>
                <Select
                  value={goalForm.goal_type}
                  onChange={(e) => setGoalForm({ ...goalForm, goal_type: e.target.value })}
                >
                  <option value="productivity">Productivity</option>
                  <option value="attendance">Attendance</option>
                  <option value="efficiency">Efficiency</option>
                  <option value="quality">Quality</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Target Value (%)</FormLabel>
                <NumberInput
                  value={goalForm.target_value}
                  onChange={(value) => setGoalForm({ ...goalForm, target_value: parseFloat(value) })}
                  min={0}
                  max={100}
                  precision={1}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Start Date</FormLabel>
                <Input
                  type="date"
                  value={goalForm.start_date}
                  onChange={(e) => setGoalForm({ ...goalForm, start_date: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>End Date</FormLabel>
                <Input
                  type="date"
                  value={goalForm.end_date}
                  onChange={(e) => setGoalForm({ ...goalForm, end_date: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={goalForm.description}
                  onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                  placeholder="Describe the performance goal..."
                  rows={3}
                />
              </FormControl>

              <Button
                colorScheme="blue"
                onClick={handleCreateGoal}
                isLoading={createGoalIsLoading}
                isDisabled={!selectedGoalEmployee || !goalForm.description}
                w="100%"
              >
                Create Goal
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// Stat Card Component
const StatCard = ({ title, value, suffix = "", color, icon, isLoading = false }) => {
  const colorScheme = {
    blue: { bg: "blue.50", color: "blue.600", border: "blue.200" },
    green: { bg: "green.50", color: "green.600", border: "green.200" },
    purple: { bg: "purple.50", color: "purple.600", border: "purple.200" },
    orange: { bg: "orange.50", color: "orange.600", border: "orange.200" },
  };

  const colors = colorScheme[color] || colorScheme.blue;

  return (
    <Card>
      <CardBody>
        <HStack spacing={4} align="center">
          <Box
            p={3}
            borderRadius="12px"
            bg={colors.bg}
            color={colors.color}
            border="1px solid"
            borderColor={colors.border}
          >
            {icon}
          </Box>
          <VStack spacing={1} align="start" flex={1}>
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              {title}
            </Text>
            {isLoading ? (
              <Box>
                <Spinner size="sm" color="blue.500" mr={2} />
                <Text fontSize="sm" color="gray.500">Loading...</Text>
              </Box>
            ) : (
              <Text fontSize="2xl" color="gray.900" fontWeight="700">
                {value}{suffix}
              </Text>
            )}
          </VStack>
        </HStack>
      </CardBody>
    </Card>
  );
};

export default PerformanceReports; 